import { useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  GOOGLE_SHEETS_MIME_TYPE,
  SUPPORTED_DOCUMENT_MIME_TYPES,
} from "@f2e/shared/constants"
import type { KnowledgeNode } from "../types"
import useSysConfig from "@/hooks/useSysConfig"
import { useToast } from "@/hooks"
import { SOMETHING_WRONG_MSG } from "@/constants/errors"
import { API_PREFIX } from "@/constants"
import { loadGoogleApiScript } from "@/lib/googleApi"
import { HTTPError } from "ky"
import {
  AddFromGoogleDriveResponseSchema,
  connectionResponseToConnection,
  makeCreateConnectionRequest,
  useCreateConnectionApi,
  useGetConnectionByIdApi,
  useGetConnectionsApi,
  useGetGoogleDrivePickerTokenByConnectionIdApi,
  useKnowledgeBaseTabularCapabilitiesApi,
} from "@/hooks/api"
import type { Response } from "@/types"
import { Connection, ConnectionStatus } from "@/types"
import { hasPendingKnowledgeBaseStatus } from "./knowledgeBasePolling"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

export interface GoogleDrivePickedFile {
  id: string
  name?: string
  mimeType?: string
  resourceKey?: string
}

interface AddFromGoogleDriveRequest {
  parent_id: string
  connection_id: number
  files: Array<{
    object_id: string
    name?: string
    mime_type?: string
    url: string
  }>
}

const KB_SUPPORTED_GOOGLE_DRIVE_MIME_TYPES = new Set<string>(
  SUPPORTED_DOCUMENT_MIME_TYPES.filter((type) => type !== "text/html")
)

const getGoogleDrivePickerMimeTypes = (allowGoogleSheets: boolean) =>
  [
    ...KB_SUPPORTED_GOOGLE_DRIVE_MIME_TYPES,
    ...(allowGoogleSheets ? [GOOGLE_SHEETS_MIME_TYPE] : []),
  ].join(",")

export function isSupportedKnowledgeBaseGoogleDriveFile(
  file: GoogleDrivePickedFile,
  allowGoogleSheets = false
): boolean {
  return (
    !!file.mimeType &&
    (KB_SUPPORTED_GOOGLE_DRIVE_MIME_TYPES.has(file.mimeType) ||
      (allowGoogleSheets && file.mimeType === GOOGLE_SHEETS_MIME_TYPE))
  )
}

export function filterSupportedKnowledgeBaseGoogleDriveFiles(
  files: GoogleDrivePickedFile[],
  allowGoogleSheets = false
): {
  supportedFiles: GoogleDrivePickedFile[]
  unsupportedFiles: GoogleDrivePickedFile[]
} {
  const supportedFiles: GoogleDrivePickedFile[] = []
  const unsupportedFiles: GoogleDrivePickedFile[] = []

  for (const file of files) {
    if (isSupportedKnowledgeBaseGoogleDriveFile(file, allowGoogleSheets)) {
      supportedFiles.push(file)
    } else {
      unsupportedFiles.push(file)
    }
  }

  return { supportedFiles, unsupportedFiles }
}

async function ensurePickerLoaded(): Promise<void> {
  await loadGoogleApiScript()
  const gapiObj = (
    window as Window & { gapi?: { load?: (m: string, cb: () => void) => void } }
  ).gapi
  if (!gapiObj || typeof gapiObj.load !== "function") {
    throw new Error("Google API client is missing")
  }
  await new Promise<void>((resolve, reject) => {
    try {
      gapiObj.load("picker", resolve)
    } catch (err) {
      reject(err)
    }
  })
  const g = (window as Window & { google?: unknown }).google as
    | Window["google"]
    | undefined
  if (!g || !g.picker) throw new Error("Google Picker library is not available")
}

function parsePickedFiles(data: unknown): GoogleDrivePickedFile[] {
  const g = (window as Window & { google?: unknown }).google as
    | Window["google"]
    | undefined
  if (!g?.picker) return []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d: any = data
  const actionKey = g.picker.Response.ACTION
  const pickedKey = g.picker.Response.DOCUMENTS
  const action = d?.[actionKey]
  if (action !== g.picker.Action.PICKED) return []
  const docs = (d?.[pickedKey] ?? []) as Array<Record<string, unknown>>
  const list = docs.reduce<GoogleDrivePickedFile[]>((acc, doc) => {
    const id = (doc.id as string) ?? (doc[g.picker.Document.ID] as string)
    const mimeType =
      (doc.mimeType as string | undefined) ??
      (doc[g.picker.Document.MIME_TYPE] as string | undefined)
    const name =
      (doc.name as string | undefined) ??
      (doc[g.picker.Document.NAME] as string | undefined)
    const resourceKey = (doc as Record<string, unknown>)["resourceKey"] as
      | string
      | undefined
    const type = doc.type as string | undefined
    const isFolder =
      type?.toLowerCase() === "folder" ||
      mimeType?.toLowerCase() === "application/vnd.google-apps.folder"
    if (!id || isFolder) return acc
    acc.push({ id, mimeType, name, resourceKey })
    return acc
  }, [])
  return list
}

function isWorkspaceNode(node: KnowledgeNode | null | undefined): boolean {
  return node?.ui_kind === "WORKSPACE"
}

function getKbIdForNode(node: KnowledgeNode | null | undefined): string | null {
  if (!node) return null
  if (isWorkspaceNode(node)) return null
  if ((node.knowledge_base_id || "").startsWith("kb-explorer:")) return null
  return node.knowledge_base_id || null
}

export interface UseGoogleDriveParams {
  root: KnowledgeNode
  findNodeById: (node: KnowledgeNode, id: string) => KnowledgeNode | null
  mergeChildren: (parentId: string, children: KnowledgeNode[]) => void
  setPendingNodeMap: React.Dispatch<
    React.SetStateAction<Map<string, { kbId: string; workspaceId: string }>>
  >
  getOrganizationIdForWorkspaceId: (workspaceId: string) => string | null
  listNodes: (params: {
    urlBuilderParams: { organizationId: string; workspaceId: string }
    searchParams?: URLSearchParams
  }) => Promise<Response<KnowledgeNode[]> | undefined>
  addFromDrive: (params: {
    urlBuilderParams: {
      organizationId: string
      workspaceId: string
      kbId: string
    }
    jsonBody: AddFromGoogleDriveRequest
  }) => Promise<Response<AddFromGoogleDriveResponseSchema> | undefined>
  connections: Connection[]
  refreshConnections: () => void
}

export interface UseGoogleDriveReturn {
  openPicker: (
    connectionId: string,
    options?: { allowGoogleSheets?: boolean }
  ) => Promise<GoogleDrivePickedFile[]>
  addFromGoogleDrive: (
    folderId: string,
    connectionId: string,
    opts?: { connectionsOverride?: Connection[] }
  ) => Promise<void>
  connectAnotherGoogleAccount: (folderId: string) => Promise<void>
}

function getGooglePickerLocale(language: string): string {
  switch (language) {
    case "zh-TW":
      return "zh-TW"
    case "ja":
      return "ja"
    default:
      return "en"
  }
}

/**
 * useGoogleDrive
 *
 * Google Drive integration for KnowledgeBase:
 * - Manage Google Drive connections list
 * - Open OAuth popup to connect a new Google account (polling-only)
 * - Open Google Picker and add selected files into a KB folder
 */
export function useGoogleDrive({
  root,
  findNodeById,
  mergeChildren,
  setPendingNodeMap,
  getOrganizationIdForWorkspaceId,
  listNodes,
  addFromDrive,
  connections,
  refreshConnections,
}: UseGoogleDriveParams): UseGoogleDriveReturn {
  const { t, i18n } = useTranslation()
  const { alertToast } = useToast()
  const sys = useSysConfig()

  const createConnectionApi = useCreateConnectionApi()

  const pickerTokenApi = useGetGoogleDrivePickerTokenByConnectionIdApi()
  const { fetchFn: getTabularCapabilities } =
    useKnowledgeBaseTabularCapabilitiesApi()

  // Keep API hook states internal; do not expose to outside UI.
  const { fetchFn: getConnectionFetchFn } = useGetConnectionByIdApi()
  const { fetchFn: getConnectionsFetchFn } = useGetConnectionsApi()
  const pollingSeqRef = useRef<Record<string, number>>({})

  const pollConnectionConnected = useCallback(
    async (
      connectionId: string,
      opts?: {
        statusBefore?: string | null
        lastConnectedAtBefore?: string | null
        updatedAtBefore?: string | null
      }
    ): Promise<boolean> => {
      const nextSeq = (pollingSeqRef.current[connectionId] ?? 0) + 1
      pollingSeqRef.current[connectionId] = nextSeq
      const seq = nextSeq
      const deadline = Date.now() + 300_000
      while (Date.now() < deadline) {
        if (pollingSeqRef.current[connectionId] !== seq) {
          return false
        }
        try {
          const connResp = await getConnectionFetchFn({
            urlBuilderParams: { connectionId },
          })
          const status = connResp?.data?.status ?? null
          const lastConnectedAt = connResp?.data?.last_connected_at ?? null
          const updatedAt = connResp?.data?.updated_at ?? null

          const wasConnected = (opts?.statusBefore || "") === "CONNECTED"
          // If we were not connected before, wait until CONNECTED.
          if (!wasConnected) {
            if (status === "CONNECTED") return true
            // Stop polling early if OAuth was canceled and the connection is still not connected
            if (
              status === "NOT_CONNECTED" &&
              opts?.updatedAtBefore !== null &&
              updatedAt !== (opts?.updatedAtBefore ?? null)
            ) {
              return false
            }
          } else {
            // For already-connected connections, status may remain CONNECTED.
            // Use last_connected_at to detect success; updated_at change without last_connected_at
            // indicates a canceled popup.
            if (status === "CONNECTED") {
              const lastConnectedChanged =
                lastConnectedAt !== (opts?.lastConnectedAtBefore ?? null)
              const updatedAtChanged =
                updatedAt !== (opts?.updatedAtBefore ?? null)
              if (lastConnectedChanged) return true
              if (updatedAtChanged) return false
            }
          }
        } catch {
          // ignore and retry until deadline
        }
        await new Promise<void>((r) => window.setTimeout(r, 1000))
      }
      alertToast({ title: t(SOMETHING_WRONG_MSG) })
      return false
    },
    [alertToast, getConnectionFetchFn, t]
  )

  const openOauthPopupAndWait = useCallback(
    async (connectionId: string): Promise<boolean> => {
      // Prefetch so polling can detect "connected but refreshed" (CONNECTED + last_connected_at changes).
      let statusBefore: string | null = null
      let lastConnectedAtBefore: string | null = null
      let updatedAtBefore: string | null = null
      try {
        const connResp = await getConnectionFetchFn({
          urlBuilderParams: { connectionId },
        })
        statusBefore = connResp?.data?.status ?? null
        lastConnectedAtBefore = connResp?.data?.last_connected_at ?? null
        updatedAtBefore = connResp?.data?.updated_at ?? null
      } catch (e) {
        console.error("[GoogleDrive] prefetch connection failed", e)
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return false
      }

      window.open(
        `${sys.serverRoot}${API_PREFIX}/connections/${connectionId}/connect?mode=popup`,
        "codeer-oauth",
        "popup,width=520,height=720"
      )

      const ok = await pollConnectionConnected(connectionId, {
        statusBefore,
        lastConnectedAtBefore,
        updatedAtBefore,
      })
      if (!ok) return false

      refreshConnections()
      return true
    },
    [
      alertToast,
      getConnectionFetchFn,
      pollConnectionConnected,
      refreshConnections,
      sys.serverRoot,
      t,
    ]
  )

  const openPicker = useCallback(
    async (connectionId: string, options?: { allowGoogleSheets?: boolean }) => {
      if (!connectionId) {
        alertToast({ title: t(SOMETHING_WRONG_MSG) })
        return []
      }

      const apiKey = sys.googlePickerApiKey
      const projectNumber = sys.googleProjectNumber
      if (!apiKey || !projectNumber) {
        alertToast({ title: t(SOMETHING_WRONG_MSG) })
        return []
      }

      let token = ""
      const fetchToken = async (): Promise<string> => {
        const tokenResp = await pickerTokenApi.fetchFn({
          urlBuilderParams: { connection_id: connectionId },
        })
        return tokenResp?.data?.token || ""
      }
      try {
        token = await fetchToken()
      } catch (e) {
        console.error("[GoogleDrive] picker token fetch failed", e)
        // If the backend cannot refresh tokens / credentials are cleared, guide user to re-auth,
        // then poll until callback completes and retry token fetch once.
        if (e instanceof HTTPError && e.response.status === 400) {
          const ok = await openOauthPopupAndWait(connectionId)
          if (ok) {
            try {
              token = await fetchToken()
            } catch (e2) {
              console.error("[GoogleDrive] picker token retry failed", e2)
            }
          }
        }
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return []
      }
      if (!token) {
        // Token missing usually means the connection is not authorized anymore.
        const ok = await openOauthPopupAndWait(connectionId)
        if (ok) {
          try {
            token = await fetchToken()
          } catch (e2) {
            console.error("[GoogleDrive] picker token retry failed", e2)
          }
        }
        alertToast({ title: t(SOMETHING_WRONG_MSG) })
        return []
      }

      try {
        await ensurePickerLoaded()
        const g = (window as Window & { google?: unknown }).google as
          | Window["google"]
          | undefined
        if (!g?.picker) throw new Error("Picker missing")

        return await new Promise<GoogleDrivePickedFile[]>((resolve) => {
          let resolved = false
          const view = new g.picker.DocsView(g.picker.ViewId.DOCS)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(false)
            .setMode(g.picker.DocsViewMode.LIST)
            .setMimeTypes(
              getGoogleDrivePickerMimeTypes(options?.allowGoogleSheets === true)
            )

          const onPickerCallback = (data: unknown) => {
            if (resolved) return
            const actionKey = g.picker.Response.ACTION
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const d: any = data
            const action = d?.[actionKey]
            if (action === g.picker.Action.CANCEL) {
              resolved = true
              resolve([])
              return
            }
            if (action !== g.picker.Action.PICKED) {
              // Ignore intermediate events (e.g. LOADED) — only resolve on PICKED/CANCEL.
              return
            }
            const picked = parsePickedFiles(data)
            resolved = true
            resolve(picked)
          }

          const picker = new g.picker.PickerBuilder()
            .setAppId(projectNumber)
            .addView(view)
            .setLocale(getGooglePickerLocale(i18n.language))
            .setOAuthToken(token)
            .setDeveloperKey(apiKey)
            .setCallback(onPickerCallback)
            .enableFeature(g.picker.Feature.MULTISELECT_ENABLED)
            .enableFeature(g.picker.Feature.SUPPORT_TEAM_DRIVES)
            .build()

          picker.setVisible(true)
        })
      } catch (e) {
        console.error("[GoogleDrive] openPicker failed", e)
        alertToast({ title: t(SOMETHING_WRONG_MSG) })
        return []
      }
    },
    [
      alertToast,
      openOauthPopupAndWait,
      pickerTokenApi,
      sys.googlePickerApiKey,
      sys.googleProjectNumber,
      i18n.language,
      t,
    ]
  )

  const addFromGoogleDrive = useCallback(
    async (
      folderId: string,
      connectionId: string,
      opts?: { connectionsOverride?: Connection[] }
    ) => {
      const folder = findNodeById(root, folderId)
      if (!folder || isWorkspaceNode(folder)) return
      const workspaceId = folder.workspace_id
      if (!workspaceId) return
      const organizationId = getOrganizationIdForWorkspaceId(workspaceId)
      if (!organizationId) return
      const kbId = getKbIdForNode(folder)
      if (!kbId) return
      if (!connectionId) return

      const connList = opts?.connectionsOverride ?? connections
      const conn = connList.find((c: Connection) => c.id === connectionId)
      if (!conn || conn.status === ConnectionStatus.NotConnected) {
        const ok = await openOauthPopupAndWait(connectionId)
        if (!ok) return
      }

      let allowGoogleSheets = false
      try {
        const capabilities = await getTabularCapabilities({
          urlBuilderParams: { organizationId, workspaceId },
        })
        allowGoogleSheets = capabilities?.data?.enabled === true
      } catch (error) {
        console.error("[GoogleDrive] tabular capabilities failed", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("knowledgeBase.treeView.addFromConnection"),
            fallbackDescription: t(
              "knowledgeBase.treeView.tabular.loadCapabilitiesFailed"
            ),
          }),
        })
        return
      }

      const picked = await openPicker(connectionId, { allowGoogleSheets })
      if (picked.length === 0) return

      const { supportedFiles, unsupportedFiles } =
        filterSupportedKnowledgeBaseGoogleDriveFiles(picked, allowGoogleSheets)

      if (unsupportedFiles.length > 0) {
        alertToast({
          title: t("knowledgeBase.treeView.addFromConnection"),
          description: t("knowledgeBase.treeView.unsupportedGoogleDriveFile", {
            files: unsupportedFiles
              .map((file) => file.name || file.id)
              .join(", "),
          }),
        })
      }

      if (supportedFiles.length === 0) return

      const files = supportedFiles.map((p) => {
        const url = `https://drive.google.com/file/d/${p.id}/view${
          p.resourceKey ? `?resourcekey=${p.resourceKey}` : ""
        }`
        return { object_id: p.id, name: p.name, mime_type: p.mimeType, url }
      })

      const resp = await addFromDrive({
        urlBuilderParams: { organizationId, workspaceId, kbId },
        jsonBody: {
          parent_id: folderId,
          connection_id: Number(connectionId),
          files,
        },
      })

      const created = resp?.data?.nodes || []
      const nodesResp = await listNodes({
        urlBuilderParams: { organizationId, workspaceId },
        searchParams: new URLSearchParams({
          parent_id: folderId,
        }),
      })
      const children = (nodesResp?.data || []).filter(
        (c) => c.node_type === "FILE"
      )
      mergeChildren(
        folderId,
        children.map((c) => ({
          ...c,
          parent_id: folderId,
          children: [],
          children_loaded: false,
          ui_kind: "NODE" as const,
          workspace_id: workspaceId,
        }))
      )

      setPendingNodeMap((prev) => {
        const next = new Map(prev)
        for (const n of created) {
          if (hasPendingKnowledgeBaseStatus(n.status))
            next.set(n.id, { kbId, workspaceId })
        }
        return next
      })
    },
    [
      alertToast,
      connections,
      findNodeById,
      getOrganizationIdForWorkspaceId,
      getTabularCapabilities,
      listNodes,
      mergeChildren,
      openOauthPopupAndWait,
      openPicker,
      root,
      setPendingNodeMap,
      t,
      addFromDrive,
    ]
  )

  const connectAnotherGoogleAccount = useCallback(
    async (folderId: string) => {
      let resp: Awaited<ReturnType<typeof createConnectionApi.fetchFn>>
      try {
        resp = await createConnectionApi.fetchFn({
          jsonBody: makeCreateConnectionRequest(
            `Google Drive (${new Date().toISOString()})`,
            "GOOGLE_DRIVE"
          ),
        })
      } catch (e) {
        console.error("[GoogleDrive] create connection failed", e)
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return
      }
      const created = resp?.data
      if (!created?.id) return

      const newConnId = String(created.id)

      let statusBefore: string | null = null
      let lastConnectedAtBefore: string | null = null
      let updatedAtBefore: string | null = null
      try {
        const connResp = await getConnectionFetchFn({
          urlBuilderParams: { connectionId: newConnId },
        })
        statusBefore = connResp?.data?.status ?? null
        lastConnectedAtBefore = connResp?.data?.last_connected_at ?? null
        updatedAtBefore = connResp?.data?.updated_at ?? null
      } catch (e) {
        console.error("[GoogleDrive] prefetch connection failed", e)
        alertToast({
          ...getUserFacingApiErrorToastProps(e, t, {
            title: t(SOMETHING_WRONG_MSG),
            fallbackDescription: t(SOMETHING_WRONG_MSG),
          }),
        })
        return
      }

      // Open provider connect URL directly
      const connectUrl = `${sys.serverRoot}${API_PREFIX}/connections/${newConnId}/connect?mode=popup`
      window.open(connectUrl, "codeer-oauth", "popup,width=520,height=720")

      // Poll connection status until CONNECTED (bounded polling).
      const ok = await pollConnectionConnected(newConnId, {
        statusBefore,
        lastConnectedAtBefore,
        updatedAtBefore,
      })
      if (!ok) return

      // Refresh connections once and proceed with Picker flow.
      refreshConnections()

      // Fetch connections for the picker (we still need them for connectionsOverride)
      const refreshed = await getConnectionsFetchFn({
        searchParams: new URLSearchParams({ limit: "9999" }),
      })
      const latestConnections = (refreshed?.data || []).map(
        connectionResponseToConnection
      )

      await addFromGoogleDrive(folderId, newConnId, {
        connectionsOverride: latestConnections,
      })
    },
    [
      addFromGoogleDrive,
      alertToast,
      createConnectionApi,
      getConnectionFetchFn,
      getConnectionsFetchFn,
      pollConnectionConnected,
      refreshConnections,
      sys.serverRoot,
      t,
    ]
  )

  return {
    openPicker,
    addFromGoogleDrive,
    connectAnotherGoogleAccount,
  }
}
